/*
 * This interceptor adds access token to all calls. It also manages auth info, specifically access token, refresh token and
 * expiration date and time. It also takes care of refreshing access token when it is about to expire.
 */
// See: https://code.amazon.com/packages/AWSNovaGeneralWebsite/blobs/mainline/--/src/services/interceptors/authInterceptor.ts

import axios from "axios";
import {StoreKeys} from "../StoreKeys";
import {getPandaTokenExchangeURL} from "./BaseURLUtils";
import {publishCountToDolphinCSM, publishLatencyToDolphinCSM} from "../../helper/PUMAMetricHelper";

const ACCESS_TOKEN_NOT_FOUND_EXCEPTION = 'ACCESS_TOKEN_NOT_FOUND_EXCEPTION';
const REFRESH_TOKEN_NOT_FOUND_EXCEPTION = 'REFRESH_TOKEN_NOT_FOUND_EXCEPTION';
// Default time for token expiry 1 hour for Panda service
const DEFAULT_TOKEN_EXPIRATION_TIME = 3600 * 1000;
// Refresh token if less than 15 minutes remaining or already expired
const REFRESH_BEFORE_TIME = 15 * 60 * 1000;

let refreshAccessTokenPromise = null;

// Export Interceptors
export const authInterceptorBuilder = (clientAxiosInstance) => {
    return {
        onBeforeRequest,
        onRequestError,
        onResponseSuccess,
        onResponseError: onResponseErrorBuilder(clientAxiosInstance),
    };
};

// Interceptors
const onBeforeRequest = async (config) => {
    try {
        applyAccessToken(config);
        return config;
    } catch (error) {
        if (error.message === ACCESS_TOKEN_NOT_FOUND_EXCEPTION) {
            await refreshAccessTokenWithRefreshToken();
            applyAccessToken(config);
            return config;
        }
        else {
            console.error(error);
            throw error;
        }
    }
};

const onRequestError = (error) => {
    throw error;
};

const onResponseSuccess = async (response) => {
    return response;
};

const onResponseErrorBuilder = (axiosInstance) => async (error) => {
    const config = error.config;
    if (!config || !config.url) {
        console.error(error);
        throw error;
    }
    if (isAuthError(error)) {
        await refreshAccessTokenWithRefreshToken();
        applyAccessToken(config);
        // Retry the request using the same axios instance for the same chain of interceptors to be used
        return await axiosInstance(config);
    } else {
        console.error(error);
        throw error;
    }
};

function isAuthError(response) {
    return response.response && response.response.status === 401;
}

function applyAccessToken(config) {
    const { sessionStorage } = window;
    let accessToken, accessTokenExp;
    config.headers = config.headers || {};
    accessToken = sessionStorage.getItem(StoreKeys.ACCESS_TOKEN);
    accessTokenExp = sessionStorage.getItem(StoreKeys.TOKEN_EXPIRATION_TIME);

    // Append access token in the header
    if (accessToken && (accessTokenExp - Date.now()) > REFRESH_BEFORE_TIME) {
        config.headers["x-amz-access-token"] = accessToken;
    } else {
        console.log(accessTokenExp);
        throw new Error(ACCESS_TOKEN_NOT_FOUND_EXCEPTION);
    }
    return config;
}

// Call to Panda for token exchange
async function refreshAccessTokenWithRefreshToken() {
    if (refreshAccessTokenPromise !== null) {
        return refreshAccessTokenPromise;
    }

    const refreshToken = window.sessionStorage.getItem(StoreKeys.REFRESH_TOKEN);
    if (!refreshToken) {
        throw new Error(REFRESH_TOKEN_NOT_FOUND_EXCEPTION);
    }

    let response;
    let startTime = Date.now();
    try {
        //Emitting metric for total number of calls and latency for access token.
        publishCountToDolphinCSM("AccessTokenRequest");
        // Request new access token using refresh token
        refreshAccessTokenPromise = axios.post(getPandaTokenExchangeURL(), {
                "requested_token_type": "access_token",
                "app_name": "com.amazon.stationbuddy",
                "source_token_type": "refresh_token",
                "source_token": refreshToken
            },
            {
                headers: { "Content-Type": "application/json" }
        });
        response = await refreshAccessTokenPromise;
        publishLatencyToDolphinCSM(startTime, "AccessTokenRequest");
    } catch (e) {
        console.log('Error occurred while fetching tokens from the auth service');
        publishLatencyToDolphinCSM(startTime, "AccessTokenRequest");
        throw e;
    }
    refreshAccessTokenPromise = null;

    response = response.data;

    // Set Expiry time for access token
    let expirationTime = DEFAULT_TOKEN_EXPIRATION_TIME;
    if (response.expires_in !== null && response.expires_in !== undefined) {
        expirationTime = +response.expires_in * 1000;
    }

    // Update auth info in local storage
    updateAuthInfo(response.access_token || "", Date.now() + expirationTime);
}

// Update token info in local Storage
export function updateAuthInfo(accessToken, expirationDateTime) {
    clearAllAuthInfo();
    const { sessionStorage } = window;
    sessionStorage.setItem(StoreKeys.ACCESS_TOKEN, accessToken);
    sessionStorage.setItem(StoreKeys.TOKEN_EXPIRATION_TIME, expirationDateTime);
}

// Clear auth info from local storage
export function clearAllAuthInfo() {
    const { sessionStorage } = window;
    sessionStorage.removeItem(StoreKeys.ACCESS_TOKEN);
    sessionStorage.removeItem(StoreKeys.TOKEN_EXPIRATION_TIME);
}



// WEBPACK FOOTER //
// ./src/components/utils/network/Interceptors.js